//----------------------------------------------------------------------------------------------------------------------
// Game Manager
//----------------------------------------------------------------------------------------------------------------------

import { AbstractEngine, Scene } from '@babylonjs/core';

// Interfaces
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';

// Classes
import type { GameEventBus } from '../classes/eventBus.ts';

// Managers
import { GameEntityManager } from './entity.ts';
import { LevelManager } from './level.ts';
import { UserInputManager } from './input.ts';

// Utils
import { isBrowser } from '../utils/capabilities.ts';
import { LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Callback function for frame updates.
 * Called once per frame with the delta time in seconds.
 */
export type FrameCallback = (deltaTime : number) => void;

/**
 * Internal representation of a registered frame callback.
 */
interface RegisteredFrameCallback
{
    id : number;
    callback : FrameCallback;
}

//----------------------------------------------------------------------------------------------------------------------

export class GameManager implements Disposable
{
    private _engine : AbstractEngine;
    private _eventBus : GameEventBus;
    private _entityManager : GameEntityManager;
    private _inputManager : UserInputManager;
    private _levelManager : LevelManager;
    private _log : LoggerInterface;
    private _boundRenderLoop : () => void;
    private _boundResizeHandler : () => void;

    /** Registered frame callbacks, executed in registration order */
    private _frameCallbacks : RegisteredFrameCallback[] = [];

    /** Counter for generating unique callback IDs */
    private _nextCallbackId = 0;

    /** Whether the game is currently paused */
    private _paused = false;

    public started = false;

    //------------------------------------------------------------------------------------------------------------------

    constructor(
        engine : AbstractEngine,
        eventBus : GameEventBus,
        entityManager : GameEntityManager,
        inputManager : UserInputManager,
        levelManager : LevelManager,
        logger ?: LoggingUtility
    )
    {
        this._engine = engine;
        this._eventBus = eventBus;
        this._entityManager = entityManager;
        this._inputManager = inputManager;
        this._levelManager = levelManager;
        this._log = logger?.getLogger('GameManager') || new SAGELogger('GameManager');

        // Store bound function references for later cleanup
        this._boundRenderLoop = this._renderLoop.bind(this);
        this._boundResizeHandler = this._resizeHandler.bind(this);

        if(isBrowser())
        {
            // Resize the engine on window resize
            window.addEventListener('resize', this._boundResizeHandler);
        }
    }

    get currentScene() : Scene | null
    {
        return this._levelManager.currentLevel?.scene ?? null;
    }

    /**
     * Whether the game is currently paused.
     * When paused, entity updates and frame callbacks are skipped, but rendering continues.
     */
    get isPaused() : boolean
    {
        return this._paused;
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a callback to be called once per frame.
     *
     * **Advanced Usage**: This is intended for systems that need frame-by-frame updates
     * outside of the entity/behavior system. For game logic, prefer using entity behaviors
     * with their `update()` method instead.
     *
     * Callbacks are executed in registration order, after entity updates but before rendering.
     * Each callback receives the delta time in seconds (typically ~0.016 for 60fps).
     *
     * @param callback
     * @returns A function to unregister the callback
     *
     * @example
     * ```typescript
     * const unsubscribe = gameManager.registerFrameCallback((deltaTime) => {
     *     // deltaTime is in seconds (e.g., 0.016 for 60fps)
     *     console.log(`Frame delta: ${deltaTime}s`);
     * });
     *
     * // Later, to stop receiving callbacks:
     * unsubscribe();
     * ```
     */
    public registerFrameCallback(callback : FrameCallback) : () => void
    {
        const id = this._nextCallbackId++;
        this._frameCallbacks.push({ id, callback });

        this._log.debug(`Registered frame callback (id: ${ id })`);

        // Return unsubscribe function
        return () =>
        {
            const index = this._frameCallbacks.findIndex((cb) => cb.id === id);
            if(index !== -1)
            {
                this._frameCallbacks.splice(index, 1);
                this._log.debug(`Unregistered frame callback (id: ${ id })`);
            }
        };
    }

    //------------------------------------------------------------------------------------------------------------------

    private _renderLoop() : void
    {
        const deltaTimeMs = this._engine.getDeltaTime();
        const deltaTime = deltaTimeMs / 1000; // Convert milliseconds to seconds

        // Always poll input (needed to unpause)
        this._inputManager.pollGamepads();

        // Skip game logic updates when paused
        if(!this._paused)
        {
            // Update entities and entity manager
            this._entityManager.$frameUpdate(deltaTime);

            // Run registered frame callbacks
            for(const { callback } of this._frameCallbacks)
            {
                try
                {
                    callback(deltaTime);
                }
                catch (err)
                {
                    this._log.error('Error in frame callback:', err);
                }
            }
        }

        // Always render (shows frozen frame when paused)
        if(this.currentScene)
        {
            this.currentScene.render();
        }
    }

    private _resizeHandler() : void
    {
        if(this.started)
        {
            this._engine.resize();
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    async start() : Promise<void>
    {
        // Start the render loop
        this._engine.runRenderLoop(this._boundRenderLoop);

        this.started = true;

        this._log.info('SkewedAspect Game Engine started successfully');
    }

    async stop() : Promise<void>
    {
        this.started = false;
        this._engine.stopRenderLoop();

        this._log.info('SkewedAspect Game Engine stopped successfully');
    }

    /**
     * Pause the game. Entity updates and frame callbacks will be skipped,
     * but rendering continues (frozen frame). Input is still polled so
     * the game can be unpaused.
     *
     * Emits a 'game:paused' event.
     *
     * @param reason - Optional reason for pausing (included in event payload)
     */
    pause(reason ?: string) : void
    {
        if(this._paused)
        {
            this._log.debug('Game is already paused');
            return;
        }

        this._paused = true;
        this._log.info('Game paused', reason ? `(reason: ${ reason })` : '');

        this._eventBus.publish({
            type: 'game:paused',
            payload: { reason },
        });
    }

    /**
     * Resume the game after being paused. Entity updates and frame callbacks
     * will resume.
     *
     * Emits a 'game:resumed' event.
     *
     * @param reason - Optional reason for resuming (included in event payload)
     */
    resume(reason ?: string) : void
    {
        if(!this._paused)
        {
            this._log.debug('Game is not paused');
            return;
        }

        this._paused = false;
        this._log.info('Game resumed', reason ? `(reason: ${ reason })` : '');

        this._eventBus.publish({
            type: 'game:resumed',
            payload: { reason },
        });
    }

    /**
     * Tears down any resources held by the GameManager
     * This handles unregistering event listeners and any other cleanup
     */
    async $teardown() : Promise<void>
    {
        this._log.info('Tearing down GameManager');

        // If we're still running, stop first
        if(this.started)
        {
            await this.stop();
        }

        // Remove event listeners
        if(isBrowser())
        {
            window.removeEventListener('resize', this._boundResizeHandler);
        }

        // Clear frame callbacks
        this._frameCallbacks = [];

        this._log.info('GameManager torn down successfully');
    }
}

//----------------------------------------------------------------------------------------------------------------------
